// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { ViewportScroller } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SuccessModalService } from '@services/success-modal.service';
import { UserService } from '@services/user.service';
import { filter, map } from 'rxjs';
import { AppEvent } from '@interfaces/interfaces';
import { User } from '@interfaces/user.interfaces';
import { AuthService } from '../../services/auth.service';
import { LoginService } from '../../services/login.service';
import { SettingsService } from '@services/settings.service';
import { EnrollmentService } from '@services/enrollment.service';
import { AuthenticationStatus } from '@interfaces/auth.interfaces';

export enum SelfEnrollmentState {
    MfaVerification,
    TokenValidation,
    TokenInvalid,
    TokenVerification,
    TokenExpired,
    DealerSetupInitial,
    DealerSetupAdditionalAdmins,
    DealerSetupSetUserPassword,
}

const SelfEnrollmentStateMachine: {
    [state in SelfEnrollmentState]: {
        next: () => SelfEnrollmentState;
        fail: (reason: string) => SelfEnrollmentState;
    };
} = {
    // expired, etc
    // validates token, sets oemCode
    [SelfEnrollmentState.TokenValidation]: {
        next: () => SelfEnrollmentState.TokenVerification,
        fail: (reason) => {
            switch (reason) {
                case 'expired':
                    return SelfEnrollmentState.TokenExpired;
                case 'invalid':
                default:
                    return SelfEnrollmentState.TokenInvalid;
            }
        },
    },
    // ask the user for their oemDealerId, ensure it matches their token
    [SelfEnrollmentState.TokenVerification]: {
        next: () => SelfEnrollmentState.DealerSetupInitial,
        fail: () => null,
    },
    [SelfEnrollmentState.DealerSetupInitial]: {
        next: () => SelfEnrollmentState.DealerSetupSetUserPassword,
        fail: () => null,
    },
    [SelfEnrollmentState.DealerSetupAdditionalAdmins]: {
        next: () => null,
        fail: () => null,
    },
    //// these states do not transition to other states
    [SelfEnrollmentState.DealerSetupSetUserPassword]: {
        // redirects to the welcome guide for signatures etc
        next: () => null,
        fail: () => null,
    },
    [SelfEnrollmentState.TokenInvalid]: {
        next: () => null,
        fail: () => null,
    },
    [SelfEnrollmentState.TokenExpired]: {
        next: () => null,
        fail: () => null,
    },
    [SelfEnrollmentState.MfaVerification]: {
        next: () => null,
        fail: () => null,
    },
};

@Component({
    selector: 'app-entrypoint',
    templateUrl: './entrypoint.component.html',
    styleUrls: ['./entrypoint.component.scss'],
})
export class EntrypointComponent implements OnInit, OnDestroy {
    protected readonly SelfEnrollmentState = SelfEnrollmentState;
    state: SelfEnrollmentState = SelfEnrollmentState.TokenValidation;

    sharedData = {
        oemCode: null,
        dealerId: null,
        dealerName: '',
        oemDealerId: '',
        currentUser: null as User,
        needsNewPassword: false,
        password: '',
        checkedSystemDocuments: [] as number[],
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        jobTitle: '',
        jacketId: null,
    };
    private loginData = {
        email: '',
        password: '',
        onSuccess: (): Promise<AppEvent[]> => null,
        onFailure: () => {
            // failure callback
        },
        onMfaStart: () => {
            // mfa process has started (a new dialog has opened, for the mfa code)
        },
        fromState: null as SelfEnrollmentState,
    };
    oemDealerId: string = null;

    public dealerId = '';
    externalToken = '';
    private _isComponentAlive = true;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly viewportScroller: ViewportScroller,

        private readonly authService: AuthService,
        private readonly enrollmentService: EnrollmentService,
        private readonly loginService: LoginService,
        private readonly settingsService: SettingsService,
        private readonly successModalService: SuccessModalService,
        private readonly userService: UserService
    ) {}

    ngOnInit(): void {
        this._isComponentAlive = true;

        this.route.queryParamMap
            .pipe(
                map((param) => {
                    if (param.has('page')) {
                        switch (param.get('page')) {
                            case 'verification':
                                this.state = SelfEnrollmentState.TokenVerification;
                                break;
                        }
                    }
                    if (param.has('jacketId')) {
                        this.sharedData.jacketId = param.get('jacketId');

                        // use the jacketId to find the dealer's oem code, for branding
                        this.enrollmentService.getEnrollmentInfoFromJacketId$(this.sharedData.jacketId).subscribe((enrollments) => {
                            if (enrollments?.oemCode) {
                                this.settingsService.setOemCode(enrollments.oemCode);
                                this.sharedData.oemCode = enrollments.oemCode;
                            }
                        });
                    }
                    return param;
                }),
                filter((param) => {
                    // on page refresh check if they have a token
                    if (!param.has('token').valueOf()) {
                        this.getUserInfoWithToken();
                    }

                    return param.has('token');
                }),
                map((param) => {
                    return param.get('token');
                })
            )
            .subscribe(async (data) => {
                if (data) {
                    this.externalToken = data;
                    localStorage.setItem('sso-token', data);
                    // redirect to remove the token from the url
                    await this.router.navigateByUrl('/login/firstUserLogging/entrypoint');
                }
            });
    }

    ngOnDestroy(): void {
        this._isComponentAlive = false;
    }

    get user(): User {
        return this.sharedData.currentUser;
    }

    async signal(event: AppEvent) {
        if ('next' === event.context) {
            const previousState = this.state;
            const nextState = SelfEnrollmentStateMachine[previousState].next();
            this.state = nextState;
        } else if ('cancel' === event.context) {
            // on cancel, send to main login form
            await this.router.navigateByUrl('/login');
        } else if ('fail' === event.context) {
            const message = event.data;
            this.state = SelfEnrollmentStateMachine[this.state].fail(message);
        } else if ('data' === event.context) {
            this.sharedData = { ...this.sharedData, ...event.data };
        } else if ('login' === event.context) {
            const data: { email; password; onSuccess; onFailure; onMfaStart } = event.data;
            this.loginData = { fromState: this.state, ...data };
            this.login();
        } else if ('goToRoute' === event.context) {
            const route = event.data;
            if (route.includes('dashboard/help/gettingstarted')) {
                this.userService.setUser({
                    ...this.userService.user,
                    needToAcceptSystemDocument: false,
                }); //defensive protection against race condition
            }
            await this.router.navigate(route);
        }
    }

    private getUserInfoWithToken(): void {
        this.externalToken = localStorage.getItem('sso-token');
    }

    private login() {
        this.loginService.authenticateUser(
            this.loginData.email,
            this.loginData.password,
            null,
            async () => {
                await this.loginSuccess();
            },
            () => {
                this.loginData.onFailure();
            },
            () => {
                this.loginData.onMfaStart();
                this.mfaCallback();
            },
            async () => {
                // the user needs a new password.
                // But we already knew that, and do not want them to have to set a password just yet.
                await this.loginSuccess();
            }
        );
    }

    private async loginSuccess() {
        const events = await this.loginData.onSuccess();
        if (events) {
            for (const event of events) {
                await this.signal(event);
            }
        }
    }

    verifyChange(event) {
        if (event.context == 'success') {
            const session: AuthenticationStatus = event.data;
            this.authService.setSessionTokens(session.logonId, session.accessToken, session.idToken, session.refreshToken);

            this.state = this.loginData.fromState;
            this.loginSuccess();
        } else if (event.context == 'newToken') {
            this.successModalService.showSuccess({
                title: 'Verification Code',
                content: 'A new code has been sent!',
            });

            // restart the login process, which sends a new code
            this.login();
        } else {
            this.loginData.onFailure();
        }
    }

    async mfaCallback(): Promise<void> {
        this.scrollToTop();
        this.state = SelfEnrollmentState.MfaVerification;
        this.loginData.onMfaStart();
    }

    scrollToTop() {
        this.viewportScroller.scrollToPosition([0, 0]);
    }
}
